import { Box, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import {
  MdAssignment,
  MdChevronRight,
  MdDescription,
  MdHomeWork,
  MdPlayCircle,
} from "react-icons/md";
import { Link } from "react-router-dom";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { courseChapterPath } from "../../utils/studentCoursePaths";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  cream: studentPalette.cream,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
};

const TYPE_META = {
  video: { label: "Video", Icon: MdPlayCircle, accent: C.secondary },
  exam: { label: "Exam", Icon: MdAssignment, accent: C.primary },
  pdf: { label: "PDF", Icon: MdDescription, accent: C.primary },
  homework: { label: "Homework", Icon: MdHomeWork, accent: C.secondary },
};

function formatDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return null;
  const mins = Math.floor(total / 60);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${mins} min`;
}

function parseDate(value) {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

function buildSubtitle(section, detail) {
  const courseName = section?.name;
  if (courseName && detail) return `${courseName} · ${detail}`;
  return courseName || detail || "";
}

function buildRecentItems(performanceData) {
  const items = [];

  (performanceData?.recent_videos || []).forEach((v) => {
    const title = v?.title || v?.over_view || "Video session";
    items.push({
      id: `video-${v.id}`,
      type: "video",
      title,
      subtitle: buildSubtitle(v.section, formatDuration(v.duration) || "Video session"),
      date: v.created_at,
      to: courseChapterPath(v.section, v.chapter_id),
    });
  });

  (performanceData?.recent_exams || []).forEach((ex) => {
    const title = ex?.name || "Exam";
    items.push({
      id: `exam-${ex.id}`,
      type: "exam",
      title,
      subtitle: buildSubtitle(
        ex.section,
        ex?.timer == 1 && ex?.time ? `${ex.time} min timed` : "Practice exam"
      ),
      date: ex.created_at,
      to: courseChapterPath(ex.section, ex.section?.laravel_through_key),
    });
  });

  (performanceData?.recent_pdfs || []).forEach((pdf) => {
    items.push({
      id: `pdf-${pdf.id}`,
      type: "pdf",
      title: pdf?.title || pdf?.over_view || "PDF lesson",
      subtitle: buildSubtitle(pdf.section, "PDF document"),
      date: pdf.created_at,
      to: courseChapterPath(pdf.section, pdf.chapter_id),
    });
  });

  (performanceData?.recent_homeworks || []).forEach((hw) => {
    items.push({
      id: `hw-${hw.id}`,
      type: "homework",
      title: hw?.name || hw?.homework || "Homework",
      subtitle: buildSubtitle(hw.section, "Homework assignment"),
      date: hw.created_at,
      to: courseChapterPath(hw.section, hw.chapter_id),
    });
  });

  return items.sort((a, b) => parseDate(b.date) - parseDate(a.date));
}

function RecentItemRow({ item }) {
  const meta = TYPE_META[item.type] || TYPE_META.video;
  const Icon = meta.Icon;
  const dateLabel = item.date ? dayjs(item.date).format("D MMM") : "—";

  return (
    <Flex
      as={Link}
      to={item.to}
      align="center"
      gap={3}
      p={3}
      bg={studentSurfaces.muted}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      _hover={{
        borderColor: C.primary,
        bg: "rgba(91, 13, 22, 0.06)",
        textDecoration: "none",
      }}
      transition="border-color 0.2s, background 0.2s"
      flexShrink={0}
    >
      <Flex
        w="52px"
        h="52px"
        bg={meta.accent}
        color={C.cream}
        borderRadius="lg"
        align="center"
        justify="center"
        direction="column"
        flexShrink={0}
        gap={0.5}
      >
        <Icon size={18} />
        <Text fontSize="8px" fontWeight="800" textTransform="uppercase" lineHeight="1">
          {dateLabel}
        </Text>
      </Flex>
      <Box minW={0} flex="1">
        <Text
          fontSize="10px"
          fontWeight="800"
          color={C.muted}
          textTransform="uppercase"
          letterSpacing="0.08em"
          mb={0.5}
        >
          {meta.label}
        </Text>
        <Text
          fontSize="sm"
          fontWeight="700"
          color={C.secondary}
          noOfLines={2}
          sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
        >
          {item.title}
        </Text>
        <Text fontSize="xs" color={C.muted} noOfLines={1}>
          {item.subtitle}
        </Text>
      </Box>
      <Box
        as="span"
        display="inline-flex"
        color={C.primary}
        flexShrink={0}
        sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
      >
        <MdChevronRight size={22} />
      </Box>
    </Flex>
  );
}

const RecentContentPanel = ({ performanceData, isLoading }) => {
  const recentItems = useMemo(() => buildRecentItems(performanceData), [performanceData]);

  return (
    <Box
      p={6}
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      h="full"
      minH={{ base: "auto", lg: "280px" }}
      display="flex"
      flexDirection="column"
      minW={0}
    >
      <Text
        fontSize="11px"
        fontWeight="800"
        letterSpacing="0.12em"
        color={C.secondary}
        textTransform="uppercase"
        mb={4}
        flexShrink={0}
        sx={{ "html[data-theme='dark'] &": { color: "var(--text-muted)" } }}
      >
        Recent content
      </Text>

      {isLoading && recentItems.length === 0 ? (
        <Flex flex="1" align="center" justify="center" py={8}>
          <Spinner size="md" color={C.primary} thickness="3px" />
        </Flex>
      ) : recentItems.length === 0 ? (
        <Text fontSize="sm" color={C.muted} lineHeight="tall">
          No recent videos, exams, PDFs, or homework yet. Your latest activity will show here.
        </Text>
      ) : (
        <Box
          className="scroll"
          flex="1"
          minH={0}
          maxH={{ base: "320px", lg: "360px" }}
          overflowY="auto"
          overflowX="hidden"
          pr={2}
          mr={-1}
          css={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--scrollbar-thumb, #5B0D16) transparent",
          }}
        >
          <VStack align="stretch" spacing={3}>
            {recentItems.map((item) => (
              <RecentItemRow key={item.id} item={item} />
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default RecentContentPanel;
